import React, { useState, useEffect } from 'react';
import { Trash2, FileText, Plus, KeyRound, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import AssignSelect from '../components/AssignSelect';
import { useDocumentViewer } from '../components/DocumentViewer';
import { sendAdminResetLink } from '../lib/passwordResetHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { fmtDateTime } from '../lib/dateFmt';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import CreateStudentProfile from './CreateStudentProfile';
import StudentProfileChat from '../components/StudentProfileChat';
import BookAppointment from '../components/BookAppointment';
import { PIPELINE_STAGES, StagePill } from '../lib/pipeline';

const DOC_LABELS = {
  class_12_certificate: 'Class 12 Certificate',
  class_12_marksheet: 'Class 12 Marksheet',
  class_10_certificate: 'Class 10 Certificate',
  class_10_marksheet: 'Class 10 Marksheet',
  id_proof: 'Student ID Proof', // legacy single ID (older records)
  id_proof_primary: 'Student ID 1 (PR / Passport)',
  id_proof_secondary: 'Student ID 2 (DL / BCeID)',
  photo: 'Student Photograph',
};

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [me, setMe] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [bookAppointmentOpen, setBookAppointmentOpen] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ college_id: '', program_name: '' });
  const [submissions, setSubmissions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  // Search + status filter for the applications table so admins can drill
  // into large lists without scrolling.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  // Edit-student dialog state (admin/manager can patch personal details
  // until the application has been sent to a college).
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Preview dialog state for "Email Application + ZIP to College".
  const [sendPreviewOpen, setSendPreviewOpen] = useState(false);
  const [sendPreviewLoading, setSendPreviewLoading] = useState(false);
  const [sendPreview, setSendPreview] = useState(null); // { recipient, default_subject, default_message, files_included, ... }
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendingNow, setSendingNow] = useState(false);
  const [recruiterInvoices, setRecruiterInvoices] = useState([]);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [editingRecruiter, setEditingRecruiter] = useState(false);
  const [recruiterDraft, setRecruiterDraft] = useState('');
  const [savingRecruiter, setSavingRecruiter] = useState(false);
  const docViewer = useDocumentViewer();

  // Derived gates — admin/manager always pass.
  const perms = me?.effective_permissions || [];
  const has = (p) => me?.role === 'admin' || me?.role === 'manager' || perms.includes(p);
  const canSeeCommissions = has('commissions.view');
  const canEdit = has('students.edit');
  const canDelete = has('students.delete');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, collegesRes, meRes, recruitersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/students`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/recruiters/lookup`, { credentials: 'include' }),
      ]);

      if (studentsRes.ok) {
        const data = await studentsRes.json();
        setStudents(data.students || []);
        setSummary(data.summary || null);
      }

      if (collegesRes.ok) {
        const data = await collegesRes.json();
        setColleges(data.colleges || []);
      }

      if (meRes.ok) {
        setMe(await meRes.json());
      }

      if (recruitersRes.ok) {
        const data = await recruitersRes.json();
        setAllRecruiters(data.recruiters || data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openDocument = (docKey) => {
    docViewer.open({
      url: `/api/admin/students/${selectedStudent.student_id}/documents/${docKey}`,
      label: docKey.replace(/_/g, ' '),
      ownerName: selectedStudent.student_name,
    });
  };

  const handleAssignCollege = async () => {
    if (!assignForm.college_id || !assignForm.program_name) {
      alert('Please select both college and program');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/assign-college`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            student_profile_id: selectedStudent.student_id,
            college_id: assignForm.college_id,
            program_name: assignForm.program_name
          })
        }
      );

      if (response.ok) {
        alert('College assigned successfully!');
        setShowDetailModal(false);
        fetchData();
      }
    } catch (err) {
      alert('Error assigning college');
    }
  };

  // Open the edit dialog seeded with the currently-selected student so staff
  // can correct typos before sending to the college.
  const openEditDialog = () => {
    if (!selectedStudent) return;
    const q = selectedStudent.qualifications;
    setEditForm({
      student_name: selectedStudent.student_name || '',
      email: selectedStudent.email || '',
      phone: selectedStudent.phone || '',
      dob: selectedStudent.dob || '',
      qualifications_summary: typeof q === 'string' ? q : q?.summary || '',
      marital_status: selectedStudent.marital_status || '',
      student_status: selectedStudent.student_status || '',
      emergency_contact_name: selectedStudent.emergency_contact_name || '',
      emergency_contact_relation: selectedStudent.emergency_contact_relation || '',
      emergency_contact_phone: selectedStudent.emergency_contact_phone || '',
      field_of_study: selectedStudent.field_of_study || '',
      preferred_location: selectedStudent.preferred_location || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selectedStudent || !editForm) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        qualifications: { summary: editForm.qualifications_summary || '' },
      };
      delete payload.qualifications_summary;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      setSelectedStudent(data);
      setEditOpen(false);
      fetchData();
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Recruiter attribution reassignment (admin or staff with students.edit).
  // Pass `null` to clear (direct application).
  const saveRecruiter = async () => {
    if (!selectedStudent) return;
    setSavingRecruiter(true);
    try {
      const value = recruiterDraft ? recruiterDraft : null;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/recruiter`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recruiter_id: value }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      setSelectedStudent({
        ...selectedStudent,
        recruiter_id: data.recruiter_id || null,
        recruiter_name: data.recruiter_name || null,
      });
      setEditingRecruiter(false);
      fetchData();
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSavingRecruiter(false);
    }
  };

  // ── Replace a single document from the Edit dialog ─────────────────────
  // Which slot key is currently uploading (spinner), and the last-touched
  // key so we can flash a "Replaced ✔" pill.
  const [replacingDocKey, setReplacingDocKey] = useState('');
  const [replacedDocKeys, setReplacedDocKeys] = useState(new Set());

  const handleReplaceDocument = async (docKey, file) => {
    if (!selectedStudent || !file) return;
    setReplacingDocKey(docKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/documents/${docKey}/replace`,
        { method: 'POST', credentials: 'include', body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || `Upload failed (HTTP ${res.status})`);
        return;
      }
      // Update the selected student in-place so the "View" opens the fresh file.
      const nextDocs = { ...(selectedStudent.documents || {}), [docKey]: data.new_path };
      setSelectedStudent({ ...selectedStudent, documents: nextDocs });
      setReplacedDocKeys((prev) => new Set(prev).add(docKey));
      // Auto-clear the "✔ Replaced" pill after 4s.
      setTimeout(() => {
        setReplacedDocKeys((prev) => {
          const next = new Set(prev);
          next.delete(docKey);
          return next;
        });
      }, 4000);
      // Refresh the full list so the row reflects the update.
      fetchData();
    } catch (err) {
      alert('Upload error: ' + err.message);
    } finally {
      setReplacingDocKey('');
    }
  };

  // Lock the edit affordance once the application has been sent to the
  // college (same statuses the backend rejects).
  const canEditSelected =
    selectedStudent && !['sent_to_college', 'approved', 'enrolled', 'rejected'].includes(selectedStudent.status);

  const openSendPreview = async () => {
    if (!selectedStudent) return;
    setSendPreviewLoading(true);
    setSendPreviewOpen(true);
    setSendPreview(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/preview-college-email`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || `Could not load preview (HTTP ${res.status})`);
        setSendPreviewOpen(false);
        return;
      }
      setSendPreview(data);
      setSendSubject(data.default_subject || '');
      setSendMessage(data.default_message || '');
    } catch (err) {
      alert('Error loading email preview: ' + err.message);
      setSendPreviewOpen(false);
    } finally {
      setSendPreviewLoading(false);
    }
  };

  const handleSendToCollege = async () => {
    if (!selectedStudent || !sendPreview) return;
    if (!sendSubject.trim()) {
      alert('Subject cannot be empty.');
      return;
    }
    setSendingNow(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/send-to-college`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: sendSubject, message: sendMessage }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || `Failed (HTTP ${response.status})`);
        return;
      }
      const missingNote = (data.files_missing || []).length
        ? `\n\nNote: ${data.files_missing.length} file(s) were missing on disk and could not be included: ${data.files_missing.join(', ')}.`
        : '';
      const statusNote = data.email_status === 'failed'
        ? `\n\nThe submission was recorded but the email could not be delivered. You can retry from the student's submission history.`
        : `\n\nSubmission ID: ${data.submission_id}`;
      alert(`${data.message}${statusNote}${missingNote}`);
      setSendPreviewOpen(false);
      setShowDetailModal(false);
      fetchData();
    } catch (err) {
      alert('Error sending to college: ' + err.message);
    } finally {
      setSendingNow(false);
    }
  };

  const fetchSubmissions = async (studentId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${studentId}/submissions`,
        { credentials: 'include' }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.submissions || [];
    } catch (_) { return []; }
  };

  const handleResendSubmission = async (submission) => {
    if (!window.confirm(`Re-send the cached submission email to ${submission.college_email}?`)) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${submission.student_id}/submissions/${submission.submission_id}/resend`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Failed'); return; }
      alert(data.email_status === 'sent'
        ? `Re-sent successfully (status: ${data.email_status}).`
        : `Re-send returned status: ${data.email_status}. Check Resend logs.`);
      // refresh inline submission list
      const fresh = await fetchSubmissions(submission.student_id);
      setSubmissions(fresh);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleFixEmail = async (student) => {
    const newEmail = window.prompt(
      `Current email: ${student.email || '(none)'}\n\nEnter the correct email for ${student.student_name || 'this student'}. A fresh activation link will be emailed.`,
      student.email || '',
    );
    if (!newEmail || !newEmail.trim()) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${student.student_id}/fix-email`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newEmail.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`Could not update: ${data.detail || res.status}`); return; }
      alert(
        data.email_sent
          ? `✓ Email updated to ${data.new_email} and activation link sent.`
          : `Email updated to ${data.new_email}, but the activation email did NOT go out.\nReason: ${data.email_error || 'unknown'}\nUse the Reset (key) icon to retry.`,
      );
      fetchData();
    } catch (err) {
      alert(`Could not update: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { color: 'bg-yellow-100 text-yellow-800', label: 'Received' },
      under_review: { color: 'bg-blue-100 text-blue-800', label: 'Under Review' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      sent_to_college: { color: 'bg-purple-100 text-purple-800', label: 'Sent to College' }
    };
    const cfg = config[status] || config.submitted;
    return <Badge className={`${cfg.color} border-0`}>{cfg.label}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-8">Loading students...</div>;
  }

  // Apply search + stage + assigned filters. The status dropdown filters
  // on `pipeline_stage`; fresh students (null/empty stage) are grouped with
  // "Received" so choosing that filter surfaces every un-progressed student.
  const norm = (s) => (s || '').toString().toLowerCase();
  const query = norm(search).trim();
  const filteredStudents = students.filter((s) => {
    if (statusFilter !== 'all') {
      const effectiveStage = s.pipeline_stage || 'received';
      if (effectiveStage !== statusFilter) return false;
    }
    if (assignedFilter === 'unassigned' && s.assigned_to) return false;
    if (assignedFilter === 'assigned' && !s.assigned_to) return false;
    if (recruiterFilter !== 'all') {
      if (recruiterFilter === 'direct') {
        if (s.recruiter_id) return false;
      } else if (s.recruiter_id !== recruiterFilter) {
        return false;
      }
    }
    if (!query) return true;
    return (
      norm(s.student_name).includes(query) ||
      norm(s.email).includes(query) ||
      norm(s.phone).includes(query) ||
      norm(s.student_id).includes(query) ||
      norm(s.recruiter_id).includes(query) ||
      norm(s.assigned_to_name).includes(query)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-serif-display text-3xl text-tge-navy">Student Applications</h2>
        {canEdit && (
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-tge-gold hover:bg-tge-gold-soft text-tge-navy font-semibold rounded-full"
            data-testid="admin-new-student-btn"
          >
            <Plus size={14} className="mr-1.5" /> New Student
          </Button>
        )}
      </div>

      {canSeeCommissions && summary && summary.students_with_commission > 0 && (
        <CommissionSummaryStrip summary={summary} />
      )}

      {/* Search + filter bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3" data-testid="students-filter-bar">
        <div className="flex-1">
          <Input
            placeholder="Search by name, email, phone, student ID, recruiter…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm"
            data-testid="students-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-3 text-sm"
          data-testid="students-status-filter"
        >
          <option value="all">All stages</option>
          {PIPELINE_STAGES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={assignedFilter}
          onChange={(e) => setAssignedFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-3 text-sm"
          data-testid="students-assigned-filter"
        >
          <option value="all">All (assigned + unassigned)</option>
          <option value="assigned">Assigned only</option>
          <option value="unassigned">Unassigned only</option>
        </select>
        <select
          value={recruiterFilter}
          onChange={(e) => setRecruiterFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-3 text-sm max-w-[220px]"
          data-testid="students-recruiter-filter"
        >
          <option value="all">All recruiters</option>
          <option value="direct">Direct applications</option>
          {Array.from(
            new Map(
              students
                .filter((s) => s.recruiter_id)
                .map((s) => [s.recruiter_id, s.recruiter_name || s.recruiter_id])
            ).entries()
          )
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([rid, rname]) => (
              <option key={rid} value={rid}>
                {rname}
              </option>
            ))}
        </select>
        <div className="text-xs text-tge-navy/60 shrink-0" data-testid="students-filter-count">
          {filteredStudents.length} of {students.length}
        </div>
      </div>

      {filteredStudents.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500" data-testid="students-empty-state">
              {students.length === 0
                ? 'No student applications yet.'
                : 'No students match your filters. Try clearing the search or status filter.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Student</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Contact</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Recruiter</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Assigned to</th>
                    {canSeeCommissions && (
                      <th className="px-3 py-2.5 text-right text-[10px] font-semibold tracking-wider text-emerald-700 uppercase">TGE</th>
                    )}
                    {canSeeCommissions && (
                      <th className="px-3 py-2.5 text-right text-[10px] font-semibold tracking-wider text-amber-700 uppercase">Recruiter</th>
                    )}
                    <th className="px-3 py-2.5 text-right text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student.student_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                        <div className="text-[11px] text-gray-500">{student.student_id}</div>
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-gray-600 max-w-[200px]">
                        <div className="truncate" title={student.email}>{student.email}</div>
                        <div className="text-gray-400">{student.phone}</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-[11px] text-gray-500">
                        {student.recruiter_id}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {student.pipeline_stage
                          ? <StagePill stage={student.pipeline_stage} testId={`row-stage-${student.student_id}`} />
                          : getStatusBadge(student.status)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                        {me?.role === 'admin' ? (
                          <AssignSelect
                            resourceType="student"
                            resourceId={student.student_id}
                            currentAssigneeId={student.assigned_to}
                            onAssigned={fetchData}
                          />
                        ) : (
                          <span className="text-xs text-tge-navy/70" data-testid={`assigned-readonly-${student.student_id}`}>
                            {student.assigned_to_name || <span className="text-tge-navy/30">Unassigned</span>}
                          </span>
                        )}
                      </td>
                      {canSeeCommissions && (
                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                          <CommissionCell value={student.commission?.college_to_tge_amount}
                            currency={student.commission?.currency}
                            status={student.commission?.status}
                            tooltip={student.commission?.explain?.college_to_tge}
                            tone="emerald"
                            unassigned={!student.commission} />
                        </td>
                      )}
                      {canSeeCommissions && (
                        <td className="px-3 py-2.5 whitespace-nowrap text-right">
                          <CommissionCell value={student.commission?.tge_to_recruiter_amount}
                            currency={student.commission?.currency}
                            status={student.commission?.status}
                            tooltip={student.commission?.explain?.tge_to_recruiter}
                            tone="amber"
                            unassigned={!student.commission} />
                        </td>
                      )}
                      <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-0.5 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-[11px] px-2"
                            data-testid={`student-row-view-${student.student_id}`}
                            onClick={async () => {
                              setSelectedStudent(student);
                              setShowDetailModal(true);
                              setRecruiterInvoices([]);
                              setSubmissions(await fetchSubmissions(student.student_id));
                              try {
                                const res = await fetch(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${student.student_id}/recruiter-invoices`,
                                  { credentials: 'include' }
                                );
                                if (res.ok) setRecruiterInvoices((await res.json()).invoices || []);
                              } catch { /* non-critical */ }
                            }}
                          >
                            View
                          </Button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Permanently delete ${student.student_name}? This cannot be undone.`)) return;
                              try {
                                const res = await fetch(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${student.student_id}`,
                                  { method: 'DELETE', credentials: 'include' }
                                );
                                if (!res.ok) throw new Error((await res.json()).detail || 'Delete failed');
                                fetchData();
                              } catch (err) {
                                alert(err.message);
                              }
                            }}
                            className={`text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-md transition ${canDelete ? '' : 'hidden'}`}
                            title="Delete student"
                            data-testid={`delete-student-btn-${student.student_id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                          {student.email && (
                            <button
                              onClick={() => sendAdminResetLink({ email: student.email, displayName: student.student_name || student.email })}
                              className="text-tge-navy hover:text-tge-gold p-1.5 rounded-md transition inline-flex items-center"
                              title="Email password-reset link"
                              data-testid={`student-reset-${student.student_id}`}
                            >
                              <KeyRound size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleFixEmail(student)}
                            className="text-tge-navy hover:text-tge-gold p-1.5 rounded-md transition inline-flex items-center"
                            title="Fix typo'd email and resend activation"
                            data-testid={`student-fix-email-${student.student_id}`}
                          >
                            <Mail size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Student Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>Student Application - {selectedStudent?.student_name}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedStudent && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBookAppointmentOpen(true)}
                      className="text-xs h-8"
                      data-testid="admin-book-appointment-btn"
                    >
                      📹 Book video call
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/export.zip`;
                        a.download = `${selectedStudent.student_id}-profile.zip`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }}
                      className="text-xs h-8"
                      data-testid="admin-export-zip-btn"
                    >
                      📦 Download ZIP
                    </Button>
                  </>
                )}
                {canEditSelected ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openEditDialog}
                    className="text-xs h-8"
                    data-testid="edit-student-btn"
                  >
                    ✏️ Edit info
                  </Button>
                ) : selectedStudent ? (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider text-tge-navy/40 bg-tge-cream-soft px-2 py-1 rounded-full"
                    data-testid="edit-locked-badge"
                    title="Personal details are locked after the application has been sent to a college."
                  >
                    🔒 Locked
                  </span>
                ) : null}
              </div>
            </DialogTitle>
            <DialogDescription>
              Review the student&apos;s documents, assign a college, chat with the student, or dispatch their application.
            </DialogDescription>
          </DialogHeader>
          
          {selectedStudent && (
            <div className="space-y-6">
              {/* Student Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Email:</span>
                  <div>{selectedStudent.email}</div>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Phone:</span>
                  <div>{selectedStudent.phone}</div>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Date of Birth:</span>
                  <div>{selectedStudent.dob}</div>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Status:</span>
                  <div>
                    {selectedStudent.pipeline_stage
                      ? <StagePill stage={selectedStudent.pipeline_stage} testId="modal-status-pill" />
                      : getStatusBadge(selectedStudent.status)}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-500">Recruiter:</span>
                  <div data-testid="modal-recruiter" className="mt-1">
                    {editingRecruiter && canEdit ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={recruiterDraft}
                          onChange={(e) => setRecruiterDraft(e.target.value)}
                          className="h-9 rounded-md border border-input bg-white px-3 text-sm min-w-[220px] max-w-[320px]"
                          data-testid="modal-recruiter-select"
                          disabled={savingRecruiter}
                        >
                          <option value="">Direct application (no recruiter)</option>
                          {allRecruiters.map((r) => (
                            <option key={r.recruiter_id} value={r.recruiter_id}>
                              {r.name} ({r.recruiter_id})
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          onClick={saveRecruiter}
                          disabled={savingRecruiter}
                          data-testid="modal-recruiter-save"
                        >
                          {savingRecruiter ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingRecruiter(false)}
                          disabled={savingRecruiter}
                          data-testid="modal-recruiter-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        {selectedStudent.recruiter_id ? (
                          <span className="text-tge-navy">
                            {selectedStudent.recruiter_name || selectedStudent.recruiter_id}
                            <span className="text-tge-navy/40 text-xs ml-2">({selectedStudent.recruiter_id})</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-tge-gold bg-tge-cream-soft border border-tge-gold/20 px-2 py-1 rounded-full">
                            Direct application
                          </span>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => {
                              setRecruiterDraft(selectedStudent.recruiter_id || '');
                              setEditingRecruiter(true);
                            }}
                            className="text-[11px] font-medium text-tge-navy/60 hover:text-tge-navy underline underline-offset-2 transition-colors"
                            data-testid="modal-recruiter-edit-btn"
                          >
                            Change
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recruiter invoices (uploaded after Commission Received) */}
              {recruiterInvoices.length > 0 && (
                <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-4" data-testid="recruiter-invoices-section">
                  <h4 className="font-semibold text-emerald-800 text-sm mb-2">Recruiter invoices ({recruiterInvoices.length})</h4>
                  <div className="space-y-2">
                    {recruiterInvoices.map((inv) => (
                      <div key={inv.invoice_id} className="flex items-center gap-3 bg-white border border-emerald-100 rounded-lg px-3 py-2 text-xs" data-testid={`recruiter-invoice-${inv.invoice_id}`}>
                        <FileText size={13} className="text-emerald-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-tge-navy truncate">
                            {inv.invoice_number ? `Invoice #${inv.invoice_number}` : inv.filename}
                            {inv.amount ? <span className="text-emerald-700"> — {inv.amount}</span> : null}
                          </div>
                          <div className="text-tge-navy/50">
                            {inv.recruiter_name} · {fmtDateTime(inv.created_at)}
                            {inv.note ? ` · "${inv.note}"` : ''}
                          </div>
                        </div>
                        <button
                          className="shrink-0 text-emerald-700 hover:underline font-semibold"
                          data-testid={`view-invoice-${inv.invoice_id}`}
                          onClick={async () => {
                            try {
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/admin/recruiter-invoices/${inv.invoice_id}/file`,
                                { credentials: 'include' }
                              );
                              if (!res.ok) { alert('Could not open invoice file'); return; }
                              const blob = await res.blob();
                              window.open(URL.createObjectURL(blob), '_blank');
                            } catch { alert('Could not open invoice file'); }
                          }}
                        >
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Emergency contact */}
              {(selectedStudent.emergency_contact_name || selectedStudent.emergency_contact_phone) && (
                <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-4" data-testid="student-emergency-contact">
                  <h4 className="font-semibold mb-2 text-rose-800">Emergency contact</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">Name:</span>
                      <div>{selectedStudent.emergency_contact_name || '—'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Relation:</span>
                      <div>{selectedStudent.emergency_contact_relation || '—'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Phone:</span>
                      <div>{selectedStudent.emergency_contact_phone || '—'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Marital status & residency */}
              {(selectedStudent.marital_status || selectedStudent.student_status) && (
                <div className="bg-tge-cream-soft/60 border border-tge-navy/5 rounded-xl p-4" data-testid="student-marital-residency">
                  <h4 className="font-semibold mb-2 text-tge-navy">Marital status & residency</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">Marital status:</span>
                      <div>{selectedStudent.marital_status || '—'}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Student status:</span>
                      <div className="capitalize">{selectedStudent.student_status || '—'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center justify-between">
                  <span>Documents</span>
                  <span className="text-[10px] font-normal text-tge-navy/50">
                    Click Replace on any row to upload a fresh copy (available even for locked profiles).
                  </span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="student-documents-grid">
                  {selectedStudent.documents && Object.entries(selectedStudent.documents).map(([key]) => {
                    const inputId = `inline-replace-doc-${key}`;
                    const isBusy = replacingDocKey === key;
                    const wasReplaced = replacedDocKeys.has(key);
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 border border-tge-navy/10 rounded-lg bg-white pr-2"
                        data-testid={`student-doc-row-${key}`}
                      >
                        <button
                          type="button"
                          onClick={() => openDocument(key)}
                          className="flex-1 flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-tge-cream-soft/40 rounded-l-lg min-w-0"
                          data-testid={`student-doc-${key}`}
                        >
                          <FileText size={13} className="opacity-70 shrink-0" />
                          <span className="truncate">{DOC_LABELS[key] || key.replace(/_/g, ' ')}</span>
                          {wasReplaced && (
                            <span className="ml-auto text-[10px] text-emerald-600 shrink-0" data-testid={`replace-doc-success-${key}`}>
                              ✔ Replaced
                            </span>
                          )}
                        </button>
                        <label
                          htmlFor={inputId}
                          className={`shrink-0 text-[11px] px-2 py-1 rounded-md border transition ${
                            isBusy
                              ? 'opacity-60 cursor-wait bg-white border-tge-navy/10'
                              : 'cursor-pointer bg-white border-tge-navy/20 hover:border-tge-navy/50 text-tge-navy'
                          }`}
                          data-testid={`replace-doc-btn-${key}`}
                        >
                          {isBusy ? 'Uploading…' : 'Replace'}
                        </label>
                        <input
                          id={inputId}
                          type="file"
                          className="hidden"
                          disabled={isBusy}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            if (f.size > 10 * 1024 * 1024) {
                              alert('File exceeds 10 MB limit');
                              return;
                            }
                            handleReplaceDocument(key, f);
                          }}
                          data-testid={`replace-doc-input-${key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Academic background */}
              <div>
                <h4 className="font-semibold mb-2">Academic background</h4>
                {(() => {
                  const q = selectedStudent.qualifications;
                  let body = '';
                  if (!q) body = '';
                  else if (typeof q === 'string') body = q;
                  else if (typeof q === 'object') body = q.summary || JSON.stringify(q, null, 2);
                  return body ? (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap bg-tge-cream-soft/60 border border-tge-navy/5 rounded-xl p-3" data-testid="student-qualifications-body">
                      {body}
                    </div>
                  ) : (
                    <div className="text-sm text-tge-muted italic" data-testid="student-qualifications-empty">
                      Not provided
                    </div>
                  );
                })()}
              </div>

              {/* Preferred Colleges */}
              <div>
                <h4 className="font-semibold mb-2">Preferred Colleges & Programs</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    Colleges:{' '}
                    {selectedStudent.preferred_colleges?.length
                      ? selectedStudent.preferred_colleges.map((cid) => {
                          const c = colleges.find((cc) => cc.college_id === cid || cc.id === cid);
                          return c?.name || cid;
                        }).join(', ')
                      : 'None selected'}
                  </p>
                  <p>
                    Programs:{' '}
                    {selectedStudent.preferred_programs?.length
                      ? selectedStudent.preferred_programs.join(', ')
                      : 'None'}
                  </p>
                </div>
              </div>

              {/* Assign College */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Assign College & Program</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Select College</Label>
                    <select
                      className="w-full border rounded-md p-2 mt-1"
                      value={assignForm.college_id}
                      onChange={(e) => setAssignForm({ ...assignForm, college_id: e.target.value, program_name: '' })}
                    >
                      <option value="">Choose college...</option>
                      {colleges.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Select Program</Label>
                    <select
                      className="w-full border rounded-md p-2 mt-1"
                      value={assignForm.program_name}
                      onChange={(e) => setAssignForm({ ...assignForm, program_name: e.target.value })}
                      disabled={!assignForm.college_id}
                    >
                      <option value="">Choose program...</option>
                      {assignForm.college_id && colleges.find(c => c.id === assignForm.college_id)?.programs?.map((p) => (
                        <option key={p.program_name} value={p.program_name}>{p.program_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button onClick={handleAssignCollege} className="mt-3 w-full">
                  Assign College
                </Button>
              </div>

              {/* Application pipeline stage — sits right below college/program assignment */}
              <div className="bg-tge-cream-soft/40 border border-tge-navy/10 rounded-xl p-4" data-testid="pipeline-stage-section">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-tge-navy text-sm">Application stage</h4>
                  {selectedStudent.pipeline_stage && <StagePill stage={selectedStudent.pipeline_stage} testId="modal-stage-pill" />}
                </div>
                <select
                  value={selectedStudent.pipeline_stage || ''}
                  disabled={updatingStage}
                  onChange={async (e) => {
                    const stage = e.target.value;
                    setUpdatingStage(true);
                    try {
                      const res = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${selectedStudent.student_id}/pipeline-stage`,
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ stage }),
                        }
                      );
                      if (!res.ok) {
                        const d = await res.json().catch(() => ({}));
                        alert(d.detail || 'Failed to update stage');
                        return;
                      }
                      setSelectedStudent((s) => ({ ...s, pipeline_stage: stage || null }));
                      setStudents((list) =>
                        list.map((s) =>
                          s.student_id === selectedStudent.student_id ? { ...s, pipeline_stage: stage || null } : s
                        )
                      );
                    } finally {
                      setUpdatingStage(false);
                    }
                  }}
                  className="w-full border border-tge-navy/15 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-tge-navy/40"
                  data-testid="pipeline-stage-select"
                >
                  <option value="">Not started</option>
                  {PIPELINE_STAGES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-tge-navy/50 mt-2">
                  {selectedStudent.pipeline_stage_updated_by?.name
                    ? <>Last changed by <strong>{selectedStudent.pipeline_stage_updated_by.name}</strong>{selectedStudent.pipeline_stage_updated_at ? ` on ${fmtDateTime(selectedStudent.pipeline_stage_updated_at)}` : ''}. </>
                    : null}
                  Stage changes are visible to the recruiter and permanently stamped in the Notes log.
                </div>
              </div>

              {/* Send to College */}
              {selectedStudent.status === 'approved' && (
                <div className="border-t pt-4">
                  <Button
                    onClick={openSendPreview}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="send-to-college-btn"
                  >
                    📧 Email Application + ZIP to College
                  </Button>
                </div>
              )}

              {/* Submission history */}
              {submissions.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2 text-sm">Submission history</h4>
                  <div className="space-y-2">
                    {submissions.map((sub) => (
                      <div
                        key={sub.submission_id}
                        className="bg-tge-cream-soft border border-tge-navy/10 rounded-xl px-4 py-3 text-xs"
                        data-testid={`submission-row-${sub.submission_id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="font-mono text-tge-navy/80 truncate">{sub.submission_id}</div>
                          <Badge className={`${sub.email_status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} border-0 text-[10px]`}>
                            {sub.email_status === 'sent' ? '✉ Email sent' : '⚠ Email failed'}
                          </Badge>
                        </div>
                        <div className="text-tge-navy/70">
                          → <strong>{sub.college_name}</strong> · {sub.college_email}
                        </div>
                        <div className="text-tge-navy/50 mt-1">
                          {sub.submitted_at ? fmtDateTime(sub.submitted_at) : ''}
                          {' · '}{(sub.files_included || []).length} files
                          {(sub.files_missing || []).length > 0 && ` · ${sub.files_missing.length} missing`}
                          {sub.submitted_by?.name && ` · by ${sub.submitted_by.name}`}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[11px] h-7 mt-2"
                          onClick={() => handleResendSubmission(sub)}
                          data-testid={`resend-submission-${sub.submission_id}`}
                        >
                          ↻ Re-send email
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat with student — auto-mirrors messages to Notes for audit */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  Chat with student
                  <span className="text-[10px] font-normal text-tge-navy/50 bg-tge-cream-soft px-2 py-0.5 rounded-full">
                    Saved to Notes automatically
                  </span>
                </h4>
                <StudentProfileChat
                  studentId={selectedStudent.student_id}
                  studentName={selectedStudent.student_name}
                  studentEmail={selectedStudent.email}
                  hasPortalAccount={Boolean(selectedStudent.user_id)}
                  currentUserId={me?.id}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {docViewer.element}

      {/* Book video call dialog — admin/staff can schedule with any student */}
      <Dialog open={bookAppointmentOpen} onOpenChange={setBookAppointmentOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-book-appointment-dialog">
          <DialogHeader>
            <DialogTitle>Book video call — {selectedStudent?.student_name}</DialogTitle>
            <DialogDescription>
              Pick a slot and the student will get a notification.
            </DialogDescription>
          </DialogHeader>
          {selectedStudent && <BookAppointment studentId={selectedStudent.student_id} />}
        </DialogContent>
      </Dialog>

      {/* Admin: Create new student dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-4xl w-[94vw] max-h-[92vh] overflow-y-auto" data-testid="admin-create-student-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-tge-navy text-2xl">Create student profile</DialogTitle>
            <DialogDescription>
              Walk-in or assisted application. Optionally credit a recruiter for commission tracking.
            </DialogDescription>
          </DialogHeader>
          <CreateStudentProfile
            adminMode
            onCancel={() => setCreateOpen(false)}
            onSuccess={(data) => {
              setCreateOpen(false);
              alert(`Student profile created. ID: ${data.student_id}`);
              fetchData();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit student personal details */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-student-dialog">
          <DialogHeader>
            <DialogTitle>Edit student information</DialogTitle>
            <DialogDescription>
              Correct typos or update details. Editable until the application has been sent to a college.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">Full name</Label>
                  <Input
                    value={editForm.student_name}
                    onChange={(e) => setEditForm({ ...editForm, student_name: e.target.value })}
                    data-testid="edit-student-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Date of birth</Label>
                  <Input
                    type="date"
                    value={editForm.dob}
                    onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
                    data-testid="edit-student-dob"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    data-testid="edit-student-email"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Phone</Label>
                  <Input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    data-testid="edit-student-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Marital status</Label>
                  <select
                    value={editForm.marital_status}
                    onChange={(e) => setEditForm({ ...editForm, marital_status: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="edit-student-marital"
                  >
                    <option value="">Select…</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Separated">Separated</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Student status</Label>
                  <select
                    value={editForm.student_status}
                    onChange={(e) => setEditForm({ ...editForm, student_status: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="edit-student-status"
                  >
                    <option value="">Select…</option>
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Field of study</Label>
                  <Input
                    value={editForm.field_of_study}
                    onChange={(e) => setEditForm({ ...editForm, field_of_study: e.target.value })}
                    data-testid="edit-student-field"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Preferred location</Label>
                  <Input
                    value={editForm.preferred_location}
                    onChange={(e) => setEditForm({ ...editForm, preferred_location: e.target.value })}
                    data-testid="edit-student-location"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Qualifications / academic summary</Label>
                <Textarea
                  rows={3}
                  value={editForm.qualifications_summary}
                  onChange={(e) => setEditForm({ ...editForm, qualifications_summary: e.target.value })}
                  data-testid="edit-student-qualifications"
                />
              </div>

              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-gray-700 mb-2">Emergency contact</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] text-gray-500">Name</Label>
                    <Input
                      value={editForm.emergency_contact_name}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact_name: e.target.value })}
                      data-testid="edit-emergency-name"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-gray-500">Relation</Label>
                    <select
                      value={editForm.emergency_contact_relation}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact_relation: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="edit-emergency-relation"
                    >
                      <option value="">Select…</option>
                      <option value="Parent">Parent</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Friend">Friend</option>
                      <option value="Relative">Relative</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-gray-500">Phone</Label>
                    <Input
                      value={editForm.emergency_contact_phone}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact_phone: e.target.value })}
                      data-testid="edit-emergency-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={savingEdit} data-testid="edit-student-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="bg-tge-navy hover:bg-tge-navy-deep text-white"
                  data-testid="edit-student-save"
                >
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Application + ZIP preview dialog */}
      <Dialog open={sendPreviewOpen} onOpenChange={setSendPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-college-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview email to college</DialogTitle>
            <DialogDescription>
              Review and edit the message before sending. The student&apos;s ZIP of documents will be attached automatically.
            </DialogDescription>
          </DialogHeader>
          {sendPreviewLoading || !sendPreview ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading preview…</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-tge-navy/10 bg-tge-cream-soft/40 p-3 text-sm space-y-1">
                <div><span className="text-gray-500">To:</span> <strong>{sendPreview.recipient}</strong>{sendPreview.contact_name ? ` (${sendPreview.contact_name})` : ''}</div>
                {sendPreview.cc?.length > 0 && (
                  <div><span className="text-gray-500">CC:</span> {sendPreview.cc.join(', ')}</div>
                )}
                <div><span className="text-gray-500">College:</span> {sendPreview.college_name}</div>
                {sendPreview.program_name && (
                  <div><span className="text-gray-500">Program:</span> {sendPreview.program_name}</div>
                )}
              </div>
              <div>
                <Label htmlFor="college-email-subject" className="text-xs text-gray-600">Subject</Label>
                <Input
                  id="college-email-subject"
                  value={sendSubject}
                  onChange={(e) => setSendSubject(e.target.value)}
                  data-testid="send-college-subject"
                />
              </div>
              <div>
                <Label htmlFor="college-email-message" className="text-xs text-gray-600">Message</Label>
                <Textarea
                  id="college-email-message"
                  value={sendMessage}
                  onChange={(e) => setSendMessage(e.target.value)}
                  rows={8}
                  className="font-mono text-[13px] leading-relaxed"
                  data-testid="send-college-message"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  A structured applicant summary (name, email, phone, program) and the ZIP attachment notice are appended automatically below your message.
                </p>
              </div>
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 text-[12px]">
                <div className="font-semibold text-indigo-900 mb-1">
                  📎 Attachment: <code className="font-mono">{sendPreview.zip_filename}</code>
                </div>
                <div className="text-indigo-800">
                  {sendPreview.files_included.length} file{sendPreview.files_included.length === 1 ? '' : 's'} will be zipped:
                  <span className="ml-1 text-indigo-700/80">{sendPreview.files_included.map((f) => DOC_LABELS[f] || f).join(', ')}</span>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setSendPreviewOpen(false)} disabled={sendingNow} data-testid="send-college-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleSendToCollege}
                  disabled={sendingNow || !sendSubject.trim()}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="send-college-confirm"
                >
                  {sendingNow ? 'Sending…' : '📧 Send to college'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentManagement;

// --- Commission cell + summary strip ---

const fmtMoney = (cur, n) => {
  if (n == null) return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return `${cur || ''} ${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim();
};

function CommissionCell({ value, currency, status, tooltip, tone, unassigned }) {
  if (unassigned) {
    return (
      <span className="text-xs text-gray-400 italic" data-testid="commission-cell-unassigned">Unassigned</span>
    );
  }
  if (status === 'stale') {
    return <span className="text-xs text-rose-500" title="Program no longer on college">Program missing</span>;
  }
  const toneClasses = tone === 'emerald'
    ? 'text-emerald-700'
    : 'text-amber-700';
  const dot = status === 'earned' ? 'bg-emerald-500' : status === 'pending' ? 'bg-amber-400' : 'bg-gray-300';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${toneClasses} cursor-help whitespace-nowrap`}
      title={tooltip || ''}
      data-testid="commission-cell"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {fmtMoney(currency, value)}
    </span>
  );
}

function CommissionSummaryStrip({ summary }) {
  const buckets = summary?.by_currency || {};
  const currencies = Object.keys(buckets);
  if (!currencies.length) return null;
  return (
    <Card data-testid="commission-summary-strip">
      <CardContent className="py-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">Commission overview</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {summary.students_with_commission} of {summary.students_total} students have an assigned program.
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {summary.students_earned} earned
              </span>
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {summary.students_pending} pending
              </span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {currencies.map((cur) => {
            const b = buckets[cur];
            return (
              <div key={cur} className="rounded-xl border border-tge-navy/10 bg-tge-cream-soft/40 p-4">
                <div className="text-[10px] uppercase tracking-wider text-tge-muted font-semibold mb-2">{cur}</div>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="TGE earns" value={fmtMoney(cur, b.tge_revenue)} tone="emerald" />
                  <Stat label="Recruiter pay" value={fmtMoney(cur, b.recruiter_payouts)} tone="amber" />
                  <Stat label="TGE net" value={fmtMoney(cur, b.tge_net_margin)} tone="navy" />
                </div>
                {b.tge_revenue_pending > 0 && (
                  <div className="text-[11px] text-gray-500 mt-2">
                    {fmtMoney(cur, b.tge_revenue_earned)} earned · {fmtMoney(cur, b.tge_revenue_pending)} pending
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-tge-navy';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-tge-muted">{label}</div>
      <div className={`font-serif-display text-lg ${color}`}>{value}</div>
    </div>
  );
}
